import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import {
  CHUNK_ERROR_PATTERN,
  hasAttemptedChunkReload,
  reloadOnChunkErrorOnce,
} from '@/utils/chunkReload';

// Catches render/loader errors for the route it's attached to via `errorElement`, including a
// failed dynamic import() from a lazy-loaded route chunk (e.g. a stale chunk hash after a new
// deploy). `vite:preloadError` in main.tsx only covers failures that go through Vite's preload
// helper — a chunk that 404s directly during React Router's own `lazy()` resolution lands here
// instead, so the same reload-once guard is checked here too.
const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isChunkError = error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
  const alreadyReloaded = hasAttemptedChunkReload();

  useEffect(() => {
    // Never shown to the user; the UI below never leaks error.message, so this is the only
    // place the real cause is visible for debugging.
    console.error('Route error boundary caught:', error);
    if (isChunkError && !alreadyReloaded) {
      reloadOnChunkErrorOnce();
    }
  }, [error, isChunkError, alreadyReloaded]);

  if (isChunkError && !alreadyReloaded) {
    // A reload is already in flight from the effect above — avoid flashing the fallback UI.
    return null;
  }

  const message = isChunkError
    ? 'A new version of this app is available. Please refresh to continue.'
    : isRouteErrorResponse(error)
      ? 'This page could not be found.'
      : 'Something went wrong. Please try again.';

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-heading text-lg font-semibold text-fg-1">Something went wrong</p>
      <p className="max-w-md text-sm text-fg-3">{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-[var(--m4-purple-700)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        Reload page
      </button>
    </div>
  );
};

export default RouteErrorBoundary;
